import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["projectBack", "projectEntry", "projectField", "modelField", "newChatField", "projectButton", "modelButton", "projectLabel", "modelLabel",
                      "projectChip", "removeButton", "projectMenu", "modelMenu", "projectList", "manageLink", "notice", "switchPanel", "modelOptions", "providerFooter", "providerMenu", "providerLabel"]
    static values = { projectPage: Boolean }

    connect() {
        const selected = Array.from(this.projectListTarget.querySelectorAll("[data-id]")).find(row => row.dataset.id === this.projectFieldTarget.value)
        this.form.dataset.sharedProject = selected?.dataset.collaborative || "false"
        this.outside = (event) => {
            const openingProject = event.target.closest?.('[data-action~="composer-choices#openProject"]')
            if (!this.element.querySelector(".composer-choice-strip").contains(event.target) &&
                !(openingProject && this.element.contains(openingProject))) this.close()
        }
        this.escape = (event) => {
            if (event.key !== "Escape" || (this.projectMenuTarget.hidden && this.modelMenuTarget.hidden)) return
            this.close()
            this.focusAnchor()?.focus()
        }
        document.addEventListener("click", this.outside)
        this.element.addEventListener("keydown", this.escape)
        this.syncProjectChip()
        this.setModel(this.modelFieldTarget.value)
    }

    disconnect() {
        document.removeEventListener("click", this.outside)
        this.element.removeEventListener("keydown", this.escape)
    }

    get form() { return this.element.closest("form") }

    get projectAnchor() { return this.projectChipTarget.hidden ? this.form.querySelector(".composer-plus") || this.modelButtonTarget : this.projectButtonTarget }

    focusAnchor() { return this.menuAnchor?.getClientRects().length ? this.menuAnchor : this.projectAnchor }

    syncProjectChip() {
        const selected = !!this.projectFieldTarget.value
        this.projectChipTarget.hidden = !selected
        this.projectButtonTarget.title = selected ? this.projectLabelTarget.textContent : "Choose project"
        this.removeButtonTarget.hidden = !selected || !!(this.form.querySelector("[name=conversation_id]")?.value && !this.newChatFieldTarget.value)
    }

    projectKeydown(event) {
        if (event.key !== "ArrowRight") return
        event.preventDefault()
        const host = this.form.querySelector(".composer-plus-menu")
        this.application.getControllerForElementAndIdentifier(host, "split-menu")?.close()
        this.openProject()
    }

    openProject() {
        this.close()
        this.projectBackTarget.hidden = false
        this.projectEntryTarget.setAttribute("aria-expanded", "true")
        this.openMenu(this.projectMenuTarget, this.projectAnchor)
        this.projectButtonTarget.setAttribute("aria-expanded", "true")
        this.projectMenuTarget.querySelector("input").focus()
    }

    backToAdd(event) {
        event.stopPropagation()
        this.close()
        const host = this.form.querySelector(".composer-plus-menu")
        this.application.getControllerForElementAndIdentifier(host, "split-menu")?.open()
        this.projectEntryTarget.focus()
    }

    removeProject() {
        this.selectProject({ currentTarget: this.projectListTarget.querySelector('[data-id=""]') })
        this.projectAnchor.focus()
    }
    get busy() { return this.form.classList.contains("is-sending") }

    close() {
        for (const menu of [this.projectMenuTarget, this.modelMenuTarget]) {
            if (menu.matches(":popover-open")) menu.hidePopover()
            menu.hidden = true
        }
        this.projectBackTarget.hidden = true
        if (this.hasProjectEntryTarget) this.projectEntryTarget.setAttribute("aria-expanded", "false")
        this.projectButtonTarget.setAttribute("aria-expanded", "false")
        this.modelButtonTarget.setAttribute("aria-expanded", "false")
    }

    toggleProject() {
        const open = this.projectMenuTarget.hidden
        this.close()
        if (open) this.openMenu(this.projectMenuTarget, this.projectAnchor)
        this.projectButtonTarget.setAttribute("aria-expanded", String(open))
        if (open) this.projectMenuTarget.querySelector("input").focus()
    }

    toggleModel() {
        const open = this.modelMenuTarget.hidden
        this.close()
        if (open) { this.setModel(this.modelFieldTarget.value); this.openMenu(this.modelMenuTarget, this.modelButtonTarget) }
        this.modelButtonTarget.setAttribute("aria-expanded", String(open))
    }

    openMenu(menu, button) {
        this.menuAnchor = button
        menu.hidden = false
        if (!menu.showPopover) return
        const anchor = button.getBoundingClientRect()
        menu.style.position = "fixed"
        menu.style.margin = "0"
        menu.style.inset = "auto"
        const width = 14 * parseFloat(getComputedStyle(document.documentElement).fontSize)
        menu.style.width = `${Math.min(width, window.innerWidth - 24)}px`
        if (!menu.matches(":popover-open")) menu.showPopover()
        const height = menu.getBoundingClientRect().height
        const below = window.innerHeight - anchor.bottom - 18
        const above = anchor.top - 18
        const top = height > below && above > below ? anchor.top - height - 6 : anchor.bottom + 6
        menu.style.left = `${Math.max(12, Math.min(anchor.left, window.innerWidth - menu.offsetWidth - 12))}px`
        menu.style.top = `${Math.max(12, Math.min(top, window.innerHeight - height - 12))}px`
    }

    search(event) {
        const query = event.target.value.trim().toLowerCase()
        this.projectListTarget.querySelectorAll("button, a").forEach(button => { button.hidden = !button.textContent.toLowerCase().includes(query) })
    }

    selectProject(event) {
        if (this.busy) return
        const button = event.currentTarget
        this.pendingProject = { id: button.dataset.id, name: button.dataset.name, model: button.dataset.model, url: button.dataset.url, collaborative: button.dataset.collaborative === "true", defaultName: button.dataset.defaultName, defaultProvider: button.dataset.defaultProvider }
        if (this.projectFieldTarget.value === this.pendingProject.id) { this.close(); return }
        if (this.projectPageValue) {
            this.form.dispatchEvent(new CustomEvent("composer-choices:before-change"))
            const url = button.dataset.workspaceUrl || button.dataset.url
            this.close()
            this.pendingProject = null
            if (window.Turbo) window.Turbo.visit(url)
            else window.location.href = url
            return
        }
        if (this.form.querySelector("[name='conversation_id']")?.value && !this.newChatFieldTarget.value) {
            this.close()
            this.switchPanelTarget.hidden = false
        } else this.confirmSwitch()
    }

    confirmSwitch() {
        if (this.busy || !this.pendingProject) return
        this.form.dispatchEvent(new CustomEvent("composer-choices:before-change"))
        const project = this.pendingProject
        this.form.dataset.sharedProject = String(project.collaborative)
        this.projectFieldTarget.value = project.id
        this.projectLabelTarget.textContent = project.id ? project.name : ""
        this.newChatFieldTarget.value = "1"
        const conversation = this.form.querySelector("[name='conversation_id']")
        if (conversation) conversation.disabled = true
        this.form.dataset.assistantComposeConversationIdValue = ""
        const defaultOption = this.modelMenuTarget.querySelector('[data-model="default"]')
        if (project.defaultName) {
            defaultOption.dataset.provider = project.defaultProvider
            defaultOption.dataset.name = project.defaultName
            defaultOption.querySelector("span").textContent = project.defaultName
        }
        this.setModel(project.model || "default")
        this.manageLinkTarget.hidden = !project.url
        if (project.url) this.manageLinkTarget.href = project.url
        this.projectListTarget.querySelectorAll("button").forEach(button => {
            const check = button.querySelector(".bi-check2")
            if (check) check.hidden = button.dataset.id !== project.id
        })
        this.switchPanelTarget.hidden = true
        this.close()
        this.form.dispatchEvent(new CustomEvent("composer-choices:changed"))
        this.noticeTarget.hidden = true
        this.noticeTarget.textContent = ""
        this.pendingProject = null
        this.syncProjectChip()
    }

    cancelSwitch() { this.switchPanelTarget.hidden = true; this.pendingProject = null }

    selectModel(event) {
        if (this.busy) return
        this.setModel(event.currentTarget.dataset.model)
        this.close()
        this.noticeTarget.hidden = true
    }

    setModel(id) {
        const option = Array.from(this.modelMenuTarget.querySelectorAll("[data-model]")).find(button => button.dataset.model === id)
        this.modelFieldTarget.value = option ? id : "default"
        const defaultOption = this.modelMenuTarget.querySelector('[data-model="default"]')
        const name = option?.dataset.name || defaultOption.dataset.name
        this.modelLabelTarget.textContent = name
        this.provider = option?.dataset.provider || defaultOption.dataset.provider
        this.modelMenuTarget.querySelectorAll("[data-model]").forEach(button => {
            const check = button.querySelector(".bi-check2")
            const isDefault = button.dataset.model === "default"
            button.hidden = !isDefault && button.dataset.name === defaultOption.dataset.name
            if (check) check.hidden = isDefault ? name !== defaultOption.dataset.name : button.dataset.model !== this.modelFieldTarget.value
        })
        this.backToModels()
    }

    showProviders() {
        this.modelOptionsTarget.hidden = true
        this.providerFooterTarget.hidden = true
        this.providerMenuTarget.hidden = false
        this.openMenu(this.modelMenuTarget, this.modelButtonTarget)
        this.providerMenuTarget.querySelector(`[data-provider="${this.provider}"]`)?.focus()
    }

    browseProvider(event) {
        this.provider = event.currentTarget.dataset.provider
        this.backToModels()
        this.modelOptionsTarget.querySelector("[data-model]:not([hidden])")?.focus()
    }

    backToModels() {
        this.modelOptionsTarget.hidden = false
        this.providerFooterTarget.hidden = false
        this.providerMenuTarget.hidden = true
        const defaultOption = this.modelMenuTarget.querySelector('[data-model="default"]')
        this.modelOptionsTarget.querySelectorAll('[data-model]').forEach(button => {
            button.hidden = button.dataset.provider !== this.provider || (button.dataset.model !== "default" && button.dataset.name === defaultOption.dataset.name)
        })
        this.providerLabelTarget.textContent = ({ openai: "OpenAI", anthropic: "Anthropic", gemini: "Google", deepseek: "DeepSeek" })[this.provider] || this.provider
        if (this.modelMenuTarget.matches(":popover-open")) this.openMenu(this.modelMenuTarget, this.modelButtonTarget)
    }

    say(message) { this.noticeTarget.textContent = message; this.noticeTarget.hidden = false }
}
